// Owner-Console defect fix (Feb 2026, Defect 2 follow-up) — WS-9
// step-up dialog.
//
// The composer previously dispatched a ``stockaily:admin_stepup_required``
// CustomEvent to a bus that had NO subscribers.  ``adminStepUp.js``
// exposed ``subscribe()`` but nothing in the app installed a
// listener, and no re-authentication dialog was ever mounted.
// Result: a genuine step-up need dead-ended the composer with a
// static "Re-authentication required" message and no recovery path.
//
// This component wires the missing side of the flow:
//
//   1. Listens on BOTH the fetch-side ``adminStepUp.subscribe()``
//      bus (used by ``inspectAdminResponse``) AND the axios-side
//      ``stockaily:admin_stepup_required`` CustomEvent (used by
//      the composer).  A single dialog serves both call sites so
//      there is no competing implementation.
//   2. On receipt, opens a Shadcn Dialog that asks for a fresh
//      6-digit TOTP code.
//   3. Submits to ``POST /api/admin/reauth/totp`` — the new
//      endpoint that refreshes ``last_authenticated_at`` +
//      ``last_totp_verified_at`` on the caller's admin session
//      via ``mark_totp_verified``.
//   4. On success closes the dialog and broadcasts
//      ``stockaily:admin_stepup_success`` so the originally
//      failing caller can auto-retry.  On cancel/failure the
//      dialog closes and the protected action stays blocked with
//      a clear reason (no silent bypass).
//
// Security notes:
//   - The dialog never persists the TOTP code.  On close the
//     input is cleared.
//   - We do not weaken or bypass the 10-minute recent-auth window;
//     we just give it a proper recovery path.
//   - Non-owner and genuinely expired sessions are rejected by
//     the reauth endpoint before the code is verified.
//   - Failing three times in a row leaves the dialog closed and
//     surfaces a clear "still blocked" state to the caller — the
//     protected action does NOT execute.
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from "react";
import api from "@/lib/api";
import { subscribe as _stepUpSubscribe } from "@/lib/adminStepUp";
import {
  Dialog, DialogContent, DialogDescription,
  DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertTriangle, Loader2, ShieldCheck } from "lucide-react";

const _STEP_UP_EVENT = "stockaily:admin_stepup_required";
const _SUCCESS_EVENT = "stockaily:admin_stepup_success";
const _CANCEL_EVENT = "stockaily:admin_stepup_cancelled";

export default function AdminStepUpDialog() {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState(null); // sanitized WS-9 code
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const inputRef = useRef(null);

  const _openFor = useCallback((codeStr) => {
    setReason(codeStr || "admin_recent_auth_required");
    setErr("");
    setCode("");
    setOpen(true);
  }, []);

  // Fetch-side subscribers (adminStepUp.inspectAdminResponse).
  useEffect(() => {
    const unsub = _stepUpSubscribe((evt) => {
      if (evt?.kind === "step_up") _openFor(evt.code);
    });
    return () => { try { unsub(); } catch (_e) { /* isolate */ } };
  }, [_openFor]);

  // Axios-side CustomEvent (from HomepageSnapshotComposer + any
  // future composer).
  useEffect(() => {
    const onEvt = (e) => _openFor(e?.detail?.code);
    window.addEventListener(_STEP_UP_EVENT, onEvt);
    return () => window.removeEventListener(_STEP_UP_EVENT, onEvt);
  }, [_openFor]);

  // Autofocus the input when the dialog opens.
  useEffect(() => {
    if (open) {
      const id = setTimeout(() => inputRef.current?.focus(), 40);
      return () => clearTimeout(id);
    }
  }, [open]);

  const _adminHeaders = useMemo(() => ({
    "X-Admin-Request": "1",
  }), []);

  const _submit = useCallback(async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const clean = (code || "").replace(/\D/g, "").slice(0, 6);
    if (clean.length !== 6 || busy) return;
    setBusy(true);
    setErr("");
    try {
      await api.post(
        "/admin/reauth/totp",
        { code: clean },
        { headers: _adminHeaders },
      );
      setOpen(false);
      setCode("");
      // Broadcast success so the pending caller can auto-retry.
      try {
        window.dispatchEvent(new CustomEvent(_SUCCESS_EVENT, {
          detail: { reason },
        }));
      } catch (_x) { /* isolate */ }
    } catch (ex) {
      const status = ex?.response?.status;
      if (status === 401) {
        setErr("Invalid or expired code. Please try again.");
      } else if (status === 403) {
        setErr("Not authorized. Please sign in again.");
      } else {
        setErr("Verification failed. Please try again.");
      }
      setCode("");
      setTimeout(() => inputRef.current?.focus(), 20);
    } finally {
      setBusy(false);
    }
  }, [busy, code, reason, _adminHeaders]);

  const _cancel = useCallback(() => {
    setOpen(false);
    setCode("");
    setErr("");
    try {
      window.dispatchEvent(new CustomEvent(_CANCEL_EVENT, { detail: { reason } }));
    } catch (_x) { /* isolate */ }
  }, [reason]);

  const _headline = reason === "admin_totp_step_up_required"
    ? "Two-factor verification required"
    : "Re-authentication required";
  const _body = reason === "admin_totp_step_up_required"
    ? "For this sensitive owner action, please re-enter the 6-digit code from your authenticator app."
    : "Your recent-auth window has expired. Please confirm your identity with the 6-digit code from your authenticator app to continue.";

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) _cancel(); }}>
      <DialogContent
        className="sm:max-w-md"
        data-testid="admin-stepup-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" />
            {_headline}
          </DialogTitle>
          <DialogDescription data-testid="admin-stepup-body">
            {_body}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={_submit} className="space-y-3">
          <Input
            ref={inputRef}
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            data-testid="admin-stepup-input"
            className="h-11 font-mono text-center tabular tracking-[0.6em] text-lg"
            placeholder="000000"
          />
          {err ? (
            <div
              className="text-xs text-[#E5484D] inline-flex items-center gap-1.5"
              data-testid="admin-stepup-error"
              role="alert"
            >
              <AlertTriangle className="w-3.5 h-3.5" /> {err}
            </div>
          ) : null}
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={_cancel}
              disabled={busy}
              data-testid="admin-stepup-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy || code.length !== 6}
              data-testid="admin-stepup-submit"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Exposed events for tests + potential callers.
export const STEP_UP_REQUIRED_EVENT = _STEP_UP_EVENT;
export const STEP_UP_SUCCESS_EVENT = _SUCCESS_EVENT;
export const STEP_UP_CANCELLED_EVENT = _CANCEL_EVENT;
